import React, { useCallback, useEffect, useState } from 'react';

import Button from '../../../components/common/Button';
import GradientButton from '../../../components/common/GradientButton';
import Modal from '../../../components/common/Modal';
import Pagination from '../../../components/common/Pagination';
import Select from '../../../components/common/Select';
import StatusBadge from '../../../components/common/StatusBadge';

import {
  getTransferRequestsApi,
  approveTransferRequestApi,
  rejectTransferRequestApi,
} from '../../../api/transferRequest.api';
import {
  TRANSFER_TYPE_LABELS,
  TRANSFER_REQUEST_STATUS_LABELS,
  TRANSFER_REQUEST_STATUS_BADGE,
} from '../../../utils/constants';
import { toJalali } from '../../../utils/jalali';

export default function TransferRequestsManagement() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('PENDING');
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });

  const [decision, setDecision] = useState(null); // { request, action: 'approve' | 'reject' }
  const [adminNote, setAdminNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: '', type: 'success' });

  const notify = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: 'success' }), 5000);
  };

  const load = useCallback(() => {
    setLoading(true);
    getTransferRequestsApi({
      status: status || undefined,
      page: pagination.page,
      limit: pagination.limit,
    })
      .then(({ data }) => {
        setRequests(data?.data || []);
        setPagination((prev) => ({
          ...prev,
          total: data?.meta?.total ?? 0,
          totalPages: data?.meta?.totalPages ?? 1,
        }));
      })
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  }, [status, pagination.page, pagination.limit]);

  useEffect(() => {
    load();
  }, [load]);

  const openDecision = (request, action) => {
    setDecision({ request, action });
    setAdminNote('');
  };

  const submitDecision = async () => {
    if (!decision) return;
    setSubmitting(true);
    try {
      const payload = { adminNote: adminNote || undefined };
      if (decision.action === 'approve') {
        await approveTransferRequestApi(decision.request.id, payload);
        notify('درخواست تأیید شد و انتقال بازیکن ثبت شد');
      } else {
        await rejectTransferRequestApi(decision.request.id, payload);
        notify('درخواست رد شد');
      }
      setDecision(null);
      load();
    } catch (err) {
      notify(err?.response?.data?.message || 'عملیات ناموفق بود', 'error');
      setDecision(null);
      load();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black text-text-dark">درخواست‌های انتقال</h1>
          <p className="mt-1 text-sm text-slate-400">
            درخواست‌هایی که مدیران باشگاه‌ها برای جذب بازیکن ثبت کرده‌اند. با تأیید، انتقال خودکار ثبت می‌شود.
          </p>
        </div>

        <Select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          className="w-48"
        >
          <option value="">همه وضعیت‌ها</option>
          {Object.entries(TRANSFER_REQUEST_STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </Select>
      </div>

      {message.text && (
        <div
          className={`rounded-xl p-3 text-sm font-bold ${
            message.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="overflow-x-auto rounded-3xl border border-slate-200 bg-white p-4">
        {loading ? (
          <div className="py-10 text-center text-slate-400">در حال بارگذاری...</div>
        ) : (
          <>
            <table className="w-full text-right text-sm">
              <thead className="border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-white">
                <tr>
                  {['بازیکن', 'کد ملی', 'از باشگاه', 'به باشگاه', 'نوع', 'درخواست‌دهنده', 'تاریخ', 'وضعیت', 'عملیات'].map((h) => (
                    <th key={h} className="px-4 py-3 text-xs font-bold text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {requests.map((r) => (
                  <tr key={r.id} className="hover:bg-emerald-50/50">
                    <td className="px-4 py-3 font-bold">{r.player?.firstName} {r.player?.lastName}</td>
                    <td className="px-4 py-3 font-mono text-xs" dir="ltr">{r.player?.nationalCode || '—'}</td>
                    <td className="px-4 py-3">{r.fromClub?.name || '—'}</td>
                    <td className="px-4 py-3 font-bold text-emerald-700">{r.toClub?.name}</td>
                    <td className="px-4 py-3">{TRANSFER_TYPE_LABELS[r.type] || r.type}</td>
                    <td className="px-4 py-3 text-xs">{r.requester?.fullName || '—'}</td>
                    <td className="px-4 py-3">{toJalali(r.createdAt)}</td>
                    <td className="px-4 py-3">
                      <StatusBadge
                        status={TRANSFER_REQUEST_STATUS_BADGE[r.status]}
                        label={TRANSFER_REQUEST_STATUS_LABELS[r.status]}
                      />
                    </td>
                    <td className="px-4 py-3">
                      {r.status === 'PENDING' ? (
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => openDecision(r, 'approve')}
                            className="rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-600"
                          >
                            تأیید
                          </button>
                          <button
                            type="button"
                            onClick={() => openDecision(r, 'reject')}
                            className="rounded-lg bg-red-50 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-100"
                          >
                            رد
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">{r.adminNote || '—'}</span>
                      )}
                    </td>
                  </tr>
                ))}
                {requests.length === 0 && (
                  <tr>
                    <td colSpan={9} className="py-8 text-center text-slate-400">درخواستی یافت نشد</td>
                  </tr>
                )}
              </tbody>
            </table>
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
            />
          </>
        )}
      </div>

      <Modal
        open={Boolean(decision)}
        onClose={() => setDecision(null)}
        title={decision?.action === 'approve' ? 'تأیید درخواست انتقال' : 'رد درخواست انتقال'}
        size="md"
      >
        {decision && (
          <div className="space-y-4">
            <p className="text-sm leading-7 text-slate-600">
              {decision.action === 'approve'
                ? `با تأیید، «${decision.request.player?.firstName} ${decision.request.player?.lastName}» از «${decision.request.fromClub?.name || '—'}» به «${decision.request.toClub?.name}» منتقل می‌شود.`
                : `درخواست باشگاه «${decision.request.toClub?.name}» برای این بازیکن رد می‌شود.`}
            </p>

            <textarea
              rows={3}
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder="توضیحات برای مدیر باشگاه (اختیاری)"
              className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none focus:border-emerald-500"
            />

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setDecision(null)}>انصراف</Button>
              <GradientButton
                variant={decision.action === 'approve' ? 'primary' : 'danger'}
                onClick={submitDecision}
                disabled={submitting}
              >
                {submitting ? 'در حال انجام...' : decision.action === 'approve' ? 'تأیید و ثبت انتقال' : 'رد درخواست'}
              </GradientButton>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
